/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { InboxOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Select, Tooltip, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { HiOutlineClipboardList, HiOutlineDocumentText, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import styled from 'styled-components';
import { uid } from 'uid';

import { useAuth } from '../../hooks/useAuth';
import { sendSolicitation } from '../../operations/solicitations';
import { getUploadProps } from '../../services/api';

const { Option } = Select;
const { Dragger } = Upload;

const ModalBody = styled.div`
  .ant-input,
  .ant-select .ant-select-selector {
    min-height: 44px !important;
    font-size: 15px !important;
    border-radius: 8px !important;
    border-color: #c8c8c8 !important;
  }

  .ant-input {
    padding: 8px 14px !important;
  }

  .ant-input::placeholder {
    color: rgba(0, 0, 0, 0.3) !important;
    font-size: 14px !important;
  }

  .ant-select .ant-select-selector {
    padding: 6px 14px !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selection-item {
    font-size: 15px !important;
  }

  .ant-select-selection-placeholder {
    font-size: 14px !important;
    color: rgba(0, 0, 0, 0.3) !important;
  }

  textarea.ant-input {
    min-height: 80px !important;
    padding: 12px 14px !important;
  }

  .ant-form-item-label > label {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.75);
  }

  .ant-form-item-explain-error {
    font-size: 13px;
    margin-top: 4px;
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-select-focused .ant-select-selector {
    border-color: var(--primary, #1890ff) !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.12) !important;
  }
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 6px;
  color: rgba(0, 0, 0, 0.3);
  cursor: help;
  vertical-align: middle;
  transition: color 0.2s;

  &:hover {
    color: var(--primary, #1890ff);
  }
`;

const InfoTooltip = ({ text }) => (
  <Tooltip title={text} trigger={['hover', 'click']} overlayStyle={{ maxWidth: 280, fontSize: 13 }}>
    <InfoIcon>
      <QuestionCircleOutlined />
    </InfoIcon>
  </Tooltip>
);

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const NfeCard = styled.div`
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 16px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #d9d9d9;
  }
`;

const NfeCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const NfeCardTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.25);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 77, 79, 0.1);
    color: #ff4d4f;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$columns || '1fr 1fr'};
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  border: 1px dashed #d9d9d9;
  background: transparent;
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TypeCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const TypeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 16px;
  border: 2px solid ${props => props.$active ? 'var(--primary, #1890ff)' : '#f0f0f0'};
  background: ${props => props.$active ? 'rgba(24, 144, 255, 0.04)' : '#fff'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary, #1890ff);
  }

  svg {
    width: 28px;
    height: 28px;
    color: ${props => props.$active ? 'var(--primary, #1890ff)' : 'rgba(0,0,0,0.35)'};
  }
`;

const TypeCardTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const TypeCardDesc = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
`;

const CreateSolicitation = ({
  isOpen,
  onClose,
  setRows,
  rows
}) => {
  const [dataSource, setDataSource] = useState([{ key: 0 }]);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [inputMethod, setInputMethod] = useState('manual');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uniqueId, setUniqueId] = useState(uid());
  const [form] = Form.useForm();

  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setUniqueId(uid());
      setDataSource([{ key: 0 }]);
      setInputMethod('manual');
      setUploadedFiles([]);
    }
  }, [isOpen, form]);

  const handleAddRow = () => {
    setDataSource([...dataSource, { key: uid() }]);
  };

  const handleRemoveRow = (key) => {
    if (dataSource.length > 1) {
      setDataSource(dataSource.filter((item) => item.key !== key));
    }
  };

  const formatCpfCnpj = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return numbers
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  };

  const formatCurrency = (value) => {
    const numbers = value.replace(/\D/g, '');
    return (Number(numbers) / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const onFinish = (values) => {
    setIsBtnLoading(true);
    const custom_data = inputMethod === 'manual'
      ? dataSource.map((item) => ({
          amount: values[`amount_${item.key}`],
          doc: values[`doc_${item.key}`],
          cod: values[`cod_${item.key}`],
          description: values[`description_${item.key}`],
          name: values[`name_${item.key}`],
        }))
      : [];

    const payload = {
      ...values,
      solicitation_type: 'NF-e',
      uid: uniqueId,
      custom_data,
      input_method: inputMethod,
    };

    sendSolicitation(payload)
      .then((response) => {
        setIsBtnLoading(false);
        onClose();
        form.resetFields();
        setDataSource([{ key: 0 }]);
        setRows([{ ...response.data }, ...rows]);
      })
      .catch(() => {
        setIsBtnLoading(false);
      });
  };

  const cnaes = user.cnaes ? user.cnaes.split(',').map((c) => c.trim()).filter(Boolean) : [];

  const renderNfeCard = (record, index) => (
    <NfeCard key={record.key}>
      <NfeCardHeader>
        <NfeCardTitle>Nota {index + 1}</NfeCardTitle>
        {dataSource.length > 1 && (
          <DeleteButton
            type="button"
            onClick={() => handleRemoveRow(record.key)}
            aria-label="Remover nota fiscal"
          >
            <HiOutlineTrash />
          </DeleteButton>
        )}
      </NfeCardHeader>

      <SectionLabel>Quem vai receber a nota?</SectionLabel>
      <FormRow $columns="1fr 1fr">
        <Form.Item
          name={`name_${record.key}`}
          label={
            <span>
              Nome ou Razão social
              <InfoTooltip text="Nome completo ou razão social de quem vai receber a nota fiscal (o tomador do serviço)." />
            </span>
          }
          rules={[{ required: true, message: 'Informe o nome' }]}
          style={{ marginBottom: 20 }}
        >
          <Input placeholder="Ex: João da Silva" />
        </Form.Item>
        <Form.Item
          name={`doc_${record.key}`}
          label={
            <span>
              CPF ou CNPJ
              <InfoTooltip text="Documento do tomador. Para pessoa física use o CPF, para empresa use o CNPJ." />
            </span>
          }
          rules={[{ required: true, message: 'Informe o CPF ou CNPJ' }]}
          style={{ marginBottom: 20 }}
        >
          <Input
            placeholder="000.000.000-00"
            maxLength={18}
            inputMode="numeric"
            onChange={(e) => {
              const formatted = formatCpfCnpj(e.target.value);
              form.setFieldsValue({ [`doc_${record.key}`]: formatted });
            }}
          />
        </Form.Item>
      </FormRow>

      <SectionLabel>Dados do serviço</SectionLabel>
      <FormRow $columns="1fr 2fr 1fr">
        <Form.Item
          name={`cod_${record.key}`}
          label={
            <span>
              Código de serviço
              <InfoTooltip text="Código do serviço prestado (Lista de Serviços da LC 116/2003)." />
            </span>
          }
          rules={[{ required: true, message: 'Selecione o código' }]}
          style={{ marginBottom: 0 }}
        >
          <Select placeholder="Selecione">
            {cnaes.map((cnae) => (
              <Option key={cnae} value={cnae}>
                {cnae}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name={`description_${record.key}`}
          label="Descrição do serviço (opcional)"
          style={{ marginBottom: 0 }}
        >
          <Input placeholder="Ex: Consultoria em TI" />
        </Form.Item>
        <Form.Item
          name={`amount_${record.key}`}
          label={
            <span>
              Valor (R$)
              <InfoTooltip text="Valor total do serviço a ser cobrado nesta nota fiscal, em reais." />
            </span>
          }
          rules={[{ required: true, message: 'Informe o valor' }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            placeholder="0,00"
            maxLength={15}
            inputMode="numeric"
            onChange={(e) => {
              const formatted = formatCurrency(e.target.value);
              form.setFieldsValue({ [`amount_${record.key}`]: formatted });
            }}
          />
        </Form.Item>
      </FormRow>
    </NfeCard>
  );

  return (
    <Modal
      title="Solicitar Emissão de NF-e"
      open={isOpen}
      onCancel={onClose}
      width={720}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} size="large" style={{ borderRadius: 8 }}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={() => form.submit()} loading={isBtnLoading} size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
          Enviar Solicitação
        </Button>,
      ]}
    >
      <ModalBody>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
      >
        <TypeCardsRow>
          <TypeCard
            type="button"
            $active={inputMethod === 'manual'}
            onClick={() => setInputMethod('manual')}
          >
            <HiOutlineClipboardList />
            <TypeCardTitle>Preencher manualmente</TypeCardTitle>
            <TypeCardDesc>Preencha os dados de cada nota fiscal</TypeCardDesc>
          </TypeCard>
          <TypeCard
            type="button"
            $active={inputMethod === 'upload'}
            onClick={() => setInputMethod('upload')}
          >
            <HiOutlineDocumentText />
            <TypeCardTitle>Enviar planilha</TypeCardTitle>
            <TypeCardDesc>Envie um arquivo Excel ou CSV</TypeCardDesc>
          </TypeCard>
        </TypeCardsRow>

        {inputMethod === 'manual' && (
          <>
            {dataSource.map((record, index) => renderNfeCard(record, index))}

            <AddButton type="button" onClick={handleAddRow}>
              <HiOutlinePlus />
              Adicionar outra nota fiscal
            </AddButton>
          </>
        )}

        {inputMethod === 'upload' && (
          <Dragger
            {...getUploadProps(
              `customer/files/solicitations/${uniqueId}`,
              (response) => {
                if(response && response.file && response.file.status === 'done') {
                  setUploadedFiles([...uploadedFiles, response.file]);
                }
                if(response && response.file && response.file.status === 'removed') {
                  setUploadedFiles(uploadedFiles.filter((file) => file.uid !== response.file.uid));
                }
              }
            )}
            style={{ marginBottom: 24 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Clique ou arraste o arquivo para fazer upload</p>
            <p className="ant-upload-hint">
              Formatos: .xlsx, .xls ou .csv (tamanho máximo: 10MB)
            </p>
          </Dragger>
        )}

        <Form.Item
          name="description"
          label={
            <span>
              Observações para o contador (opcional)
              <InfoTooltip text="Use este campo para informar algo relevante, como prazos ou detalhes específicos sobre os serviços." />
            </span>
          }
          style={{ marginTop: 24, marginBottom: 0 }}
        >
          <Input.TextArea
            rows={2}
            placeholder="Se quiser, escreva aqui alguma informação extra sobre esta solicitação..."
          />
        </Form.Item>
      </Form>
      </ModalBody>
    </Modal>
  );
};

export default CreateSolicitation;
